import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { DryRunFrame } from '@/components/admin/experiences/dry-run-frame';
import type { ProtocolLinkRequest } from '@/components/admin/experiences/experience-blockers';
import { ExperienceSaveBlockers } from '@/components/admin/experiences/experience-blockers';
import { ExperiencePersonaTab } from '@/components/admin/experiences/experience-persona-tab';
import { ExperienceTimelineTab } from '@/components/admin/experiences/experience-timeline-tab';
import {
  ExperienceVariableScope,
  useExperienceVariableScope,
} from '@/components/admin/experiences/experience-variable-scope';
import { ProtocolReadingLink } from '@/components/admin/experiences/protocol-reading-link';
import { NOYAU_TEXTS_QUERY_KEY } from '@/components/admin/reglages/noyau-texts-editor';
import type {
  PointerBoxHandle,
  SaveBoxHandle,
} from '@/components/admin/versioned/versioned-editor';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { buttonVariants } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { useAdminSession } from '@/lib/admin-session';
import {
  checkExperience,
  experiencesApi,
  fetchNoyauTexts,
  fetchPersonas,
  fetchProtocols,
} from '@/lib/api';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { EXPERIENCE_DRAFT_KIT } from '@/lib/experience-draft';
import type { ExperiencePlace } from '@/lib/experience-labels';
import { experienceRefusalSentence } from '@/lib/experience-labels';
import { experienceContrastWarnings } from '@/lib/experience-theme';
import { EXPERIENCE_WORDING } from '@/lib/experience-wording';
import { variableNamer } from '@/lib/variable-pill';
import type {
  ExperienceContent,
  ExperienceDetail,
  ExperienceDraftContent,
  ExperienceReferenceKind,
  ExperienceReferenceState,
  ExperienceVersion,
  VersionSummary,
} from '@matter_extractor/shared';
import {
  EXPERIENCE_DESCRIPTION_MAX,
  EXPERIENCE_NAME_MAX,
  NOYAU_TEXTS_ID,
  experienceProductionBlockers,
  protocolPageOf,
} from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * L'éditeur d'une expérience : le patron de §4 en disposition large à onglets,
 * plus les surfaces de §9.1 et §10.2.
 *
 * Everything this screen does that the three editors before it do identically -
 * the head, the history, « utilisé dans », the retirement, the three boxes, the
 * debounced autosave and its optimistic control, the rename on blur - is
 * `VersionedEditor`. What is left here is the six things the layer asks of
 * every consumer plus the openings of §9.1: the tabs, the head made of fields,
 * the breadcrumb, « Créer un lien », the dry run and the two kinds of refusal.
 *
 * ## The tabs, and why `renderFields` is absent
 *
 * §9.1 gives this editor three tabs and the layer appends the third: under
 * `layout: 'tabs'` the object's surface IS its tabs, so declaring a
 * `renderFields` that returned null would be writing a slot to say there is
 * none (`versioned-editor.tsx`).
 *
 * ## What the save box reads, and why it is ONE request
 *
 * §9.1 makes the box read `POST /api/experiences/check` before it draws: two of
 * the four refusals need the base, and the AVERTISSEMENT it also carries is not
 * a refusal and so can never arrive as a 409. The request is fired with
 * `alsoProduction: true` and fired AT MOST once per opening, which is not a
 * shortcut:
 * the answer to `true` is a strict superset of the answer to `false` - only
 * `blockers` depends on the flag, `problems` and `warnings` do not - so the
 * checkbox chooses what the box DISPLAYS rather than what it asks. One round
 * trip instead of one per toggle, and the box never blinks between two answers
 * that agree.
 *
 * While the answer is in flight nothing is claimed and the button stays armed,
 * the régime `renderPointerBlockers` already holds in the layer: the server
 * refuses either way (§9.1), so what is at stake is a round trip and not a
 * guarantee - and a 409 that lands anyway is worded by `ownRefusal`.
 *
 * ## Read at the moment of the gesture, and never served from the cache
 *
 * The facts a dry run answers live on OTHER screens - a protocol given its
 * reading and passed to production in its own editor, perhaps in the tab that
 * « Ouvrir le protocole » opened - and N36 wants them read when the gesture is
 * made. So both dry runs (`check`, `pointerCheck`) keep nothing: `staleTime`
 * and `gcTime` at zero, where the client's default of 30 s would hand a box
 * reopened a minute later the refusal of the first opening (review of !474);
 * and they read again when this tab comes back to the front, the way back
 * from that other tab. While such a re-read runs, the previous answer stays,
 * greyed and said « vérification en cours », still blocking what it blocked
 * (`DryRunFrame`, second review of !474) - dropped, it re-armed a button whose
 * click the server then refused.
 *
 * ## « Passer en production » is judged HERE and not by the server
 *
 * The pointer box aims at an OLD version, whose references are not the ones
 * being typed, and the layer hands its content to `renderPointerBlockers`
 * synchronously. So the blockers are computed by the pure rule of `shared` -
 * the very one the server runs - against a lookup built from the two libraries,
 * exactly as `protocol-editor.tsx` does with its criteria. Nothing is claimed
 * before both libraries have been READ: `experienceProductionBlockers` reports
 * an id it cannot resolve as `reference-unknown`, so running it against empty
 * lists would refuse the gesture on every reference of the experience - a red
 * box that clears itself a second later, which is worse than no box.
 *
 * Three blockers are the SERVER's, read off its dry run of that version: the
 * cards know neither a protocol's section Analyste - its levels and its readings,
 * #154 and #196 - nor its directions (#158) - see `renderPointerBlockers`.
 *
 * ## The three cache keys, and the two it shares
 *
 * `['experience', email, id]` holds an `ExperienceDetail`; `['experiences',
 * email]` holds the card list. `['protocols', email]` and `['personas', email]`
 * are the keys the two other libraries already write, deliberately: same
 * fetcher, same schema, same shape, so a protocol renamed in its own library
 * shows up here without a reload. The trap of issue #16 is two readers agreeing
 * on a key and disagreeing on what is under it, which is the opposite of this.
 */

export interface ExperienceEditorProps {
  /** The entity, or null for an experience that does not exist yet. */
  detail: ExperienceDetail | null;
  /** A version opened read-only by « Voir », or null. */
  viewing: ExperienceVersion | null;
  /** The tab the URL names - the layer resolves an unknown one onto the first. */
  tab: string | undefined;
  /** The page `?page=` names - what the scene of §9.1 puts at its centre. */
  pageId: string | null;
  onSelectTab: (id: string) => void;
  /** Where « Ouvrir » on a refused line goes: `?tab=` plus `?page=`. */
  onOpenPlace: (place: ExperiencePlace) => void;
  onCloseView: () => void;
  onView: (version: VersionSummary) => void;
  /** Re-read the entity from the server - what the stale-draft box offers. */
  onReload: () => void;
  /**
   * Select the name field on opening (§4.2): true on the editor a duplication
   * has just navigated to, which the route reads off `?copie`.
   */
  selectName?: boolean;
}

export function ExperienceEditor({
  detail,
  viewing,
  tab,
  pageId,
  onSelectTab,
  onOpenPlace,
  onCloseView,
  onView,
  onReload,
  selectName = false,
}: ExperienceEditorProps) {
  const { email } = useAdminSession();
  const navigate = useNavigate();

  /**
   * What the save box is about to save, handed over by the layer when the box
   * opens and taken back when it closes - see `onSaveBox`.
   */
  const [saving, setSaving] = useState<SaveBoxHandle<ExperienceContent> | null>(null);
  /**
   * Counts the openings of the boxes, in the keys of their dry runs: each
   * opening reads its OWN answer (second review of !474). A `gcTime` of zero
   * drops a closed box's answer on the next tick only, so a box reopened on
   * the same content within it would otherwise be handed the last one - and
   * draw a « vérification en cours » over a reading it never had.
   */
  const [openings, setOpenings] = useState(0);
  const onSaveBox = (opened: SaveBoxHandle<ExperienceContent> | null) => {
    setSaving(opened);
    if (opened !== null) setOpenings((count) => count + 1);
  };

  const check = useQuery({
    queryKey: [
      'experience-check',
      email,
      detail?.id ?? 'nouveau',
      openings,
      saving?.content ?? null,
    ],
    queryFn: () => {
      if (saving === null) throw new Error('no content to check');
      // `alsoProduction: true` is the SUPERSET - see the head of this file.
      return checkExperience({ content: saving.content, alsoProduction: true });
    },
    enabled: saving !== null,
    retry: false,
    // Read at the gesture, never from the cache - see the head of this file.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  });
  /** Read again in the background, over an answer already drawn: kept, greyed (`DryRunFrame`). */
  const recheckingSave = check.isFetching && check.data !== undefined;

  /**
   * The version a pointer box moves and the pointer it moves, handed over by
   * the layer when the box opens - see `onPointerBox` and « Passer en
   * production » in the head of this file.
   */
  const [pointing, setPointing] = useState<PointerBoxHandle | null>(null);
  const pointed = pointing?.tag === 'production' ? pointing.target : null;
  const onPointerBox = (opened: PointerBoxHandle | null) => {
    setPointing(opened);
    if (opened !== null) setOpenings((count) => count + 1);
  };

  /*
    The dry run of the version « Passer en production » moves (noyau.md §3.7,
    #154): the SERVER's blockers for that content, `analyst-levels-missing` and
    `analyst-reading-missing` since #196 (analyste.md §3.6) and, since #158,
    `protocol-variable-orphan` among them (§8.6) - the three kinds of blocker the
    cards cannot judge, a protocol's section Analyste and its directions living in
    its versions. The version is read here and not taken from the layer, which reads
    it for `renderPointerBlockers` during a render: a second GET of one version, on a
    gesture an operator makes a few times a day.
  */
  const pointerCheck = useQuery({
    queryKey: [
      'experience-check',
      email,
      detail?.id ?? 'nouveau',
      'pointeur',
      openings,
      pointed?.id ?? null,
    ],
    queryFn: async () => {
      if (detail === null || pointed === null) throw new Error('no version to check');
      const version = await experiencesApi.readVersion(detail.id, pointed.id);
      return checkExperience({ content: version.content, alsoProduction: true });
    },
    enabled: detail !== null && pointed !== null,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  });

  const protocols = useQuery({
    queryKey: ['protocols', email],
    queryFn: fetchProtocols,
    retry: false,
  });

  const personas = useQuery({
    queryKey: ['personas', email],
    queryFn: fetchPersonas,
    retry: false,
  });

  /*
    The texts of the noyaux, the third reference an experience may cite
    (noyau.md §3.5, #151) - under the key Réglages > Noyaux writes, so a version
    passed to production there is known here without a reload. Null while no
    version exists at all, which the production check reads as unknown.
  */
  const noyauTexts = useQuery({
    queryKey: [NOYAU_TEXTS_QUERY_KEY, email, NOYAU_TEXTS_ID],
    queryFn: fetchNoyauTexts,
    retry: false,
  });

  /** The three references READ - nothing is refused before they are (see the head). */
  const referencesRead =
    protocols.data !== undefined && personas.data !== undefined && noyauTexts.data !== undefined;

  /**
   * The protocol the RECORD's protocol page plays, for the names of the save
   * box (§8.6, D3) - the tabs read the draft's through `ExperienceVariableScope`,
   * under the same cache key, so the two never fetch twice.
   */
  const onRecord = detail?.draft?.content ?? detail?.developmentContent ?? null;
  const recordScope = useExperienceVariableScope(
    onRecord === null ? null : (protocolPageOf(onRecord)?.protocol ?? null),
    detail?.id ?? null,
  );

  /**
   * What the production check knows about one referenced object (§4).
   *
   * ONE lookup for both kinds, because the rule of `shared` asks one question
   * of two libraries: is there a version in production, and has the entity been
   * retired.
   */
  const lookup = (
    kind: ExperienceReferenceKind,
    id: string,
  ): ExperienceReferenceState | undefined => {
    if (kind === 'noyau-texts') {
      // A singleton: never retired, and unknown until its first version (N8).
      const texts = noyauTexts.data ?? null;
      return texts === null
        ? undefined
        : { name: texts.name, hasProduction: texts.production !== null, retiredAt: null };
    }
    const cards =
      kind === 'protocol' ? (protocols.data?.protocols ?? []) : (personas.data?.personas ?? []);
    const card = cards.find((one) => one.id === id);
    if (card === undefined) return undefined;
    return { name: card.name, hasProduction: card.production !== null, retiredAt: card.retiredAt };
  };

  /**
   * « Ouvrir le protocole » (analyste.md §3.6, noyau.md §3.7, §8.6 of the refonte),
   * closing the box it sits in through that box's `leave` - in a NEW tab while the
   * experience does not exist yet (`ProtocolReadingLink`). The view it opens
   * is the request's: the section Analyste, or the conduite of an orphan direction.
   */
  const protocolLink = (leave: (() => void) | undefined) => (request: ProtocolLinkRequest) => (
    <ProtocolReadingLink {...request} newTab={detail === null} onLeave={leave} />
  );

  return (
    <VersionedEditor<ExperienceContent, ExperienceDraftContent, ExperienceDraft>
      wording={EXPERIENCE_WORDING}
      api={experiencesApi}
      kit={EXPERIENCE_DRAFT_KIT}
      keys={{ detail: 'experience', list: 'experiences' }}
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      // §9.1: the three panels of §4 move into a tab, which is the experience's
      // logement of the wide layout the protocol lodges in a drawer.
      layout="tabs"
      // Spread and not `tab={tab}`: `exactOptionalPropertyTypes` makes an
      // explicit `undefined` a type error, so an absent parameter is an absent
      // KEY - the same spelling the grid uses for its optional card slots.
      {...(tab === undefined ? {} : { tab })}
      onSelectTab={onSelectTab}
      onCreated={(experienceId) =>
        void navigate({ to: '/admin/experiences/$experienceId', params: { experienceId } })
      }
      selectName={selectName}
      breadcrumb={
        <p className="text-gray-600 text-sm">
          <Link to="/admin/experiences" className="text-bleu-nuit underline underline-offset-2">
            Expériences
          </Link>
          <span className="mx-2">›</span>
          {detail?.name ?? EXPERIENCE_WORDING.newTitle}
        </p>
      }
      /*
        The name AND the short description in the head, above the tabs (§9.1,
        revue du 2026-09-11): « ils appartiennent à l'expérience, pas à sa
        timeline, et restent sous les yeux quel que soit l'onglet ouvert ». It
        is a function of the binding because the second field's value is IN the
        draft, and `{ ...draft, name }` does not type back to a generic draft.
      */
      headerFields={(binding) => ({
        name: {
          label: 'Nom de l’expérience',
          value: binding.draft.name,
          hint: 'Ce que l’équipe lit dans la bibliothèque et dans un lien.',
          maxLength: EXPERIENCE_NAME_MAX,
          onChange: (name) => binding.onDraft({ ...binding.draft, name }),
        },
        second: {
          label: 'Courte description',
          value: binding.draft.description,
          hint: 'Ce que la personne lit sur la page de choix d’un lien.',
          maxLength: EXPERIENCE_DESCRIPTION_MAX,
          onChange: (description) => binding.onDraft({ ...binding.draft, description }),
        },
      })}
      /*
        « Créer un lien » (§9.1) - un `<Link>` TYPÉ depuis #59.

        It was a bare anchor while Réglages > Partage was being built in
        parallel: `to="/admin/reglages/partage"` did not compile against the
        route tree of the day. The route exists now, and its `validateSearch`
        declares the one parameter of §9.1 (`experience`), so the pre-fill
        travels as a typed search object rather than as a hand-encoded query
        string - the id is no longer escaped here, because the router owns the
        encoding. It is absent before the first save, because a link plays an
        experience and there is none to play yet.
      */
      renderHeaderExtra={() =>
        detail === null ? null : (
          <Link
            to="/admin/reglages/partage"
            search={{ experience: detail.id }}
            data-testid={EXPERIENCE_IDS.createLink}
            className={`${buttonVariants({ variant: 'outline', size: 'sm' })} no-underline`}
          >
            Créer un lien
          </Link>
        )
      }
      onSaveBox={onSaveBox}
      renderSaveBlockers={(draft, alsoProduction) => {
        const answer = check.data;
        // Nothing claimed before the first answer - see the head.
        if (answer === undefined) return null;
        const blockers = alsoProduction ? answer.blockers : [];
        const refusals = answer.problems.length + blockers.length;
        /*
          Le contraste de §9.4, REPRIS dans la boîte : « l'avertissement se
          répète dans la boîte d'enregistrement, parce que c'est le dernier
          moment où on le lit avant qu'une personne recrutée le voie ». It is
          computed from the DRAFT and not from the dry run's answer, because it
          is not a fact the server has an opinion about - it is arithmetic on
          two colours - and it is the very same function the bar spends, so the
          two surfaces can never warn about different pairs.

          Depuis #58 il compte AUSSI les surcharges de page (§9.4) : une page
          qui rend son texte discret illisible se lit ici, nommée, et la boîte
          de l'éditeur de page rend exactement la même liste. Une page qui ne
          surcharge rien n'ajoute rien - `pageContrastWarnings` filtre sur la
          phrase - donc rien ne bouge pour une expérience qui n'a jamais touché
          au visuel d'une page.
        */
        const contrast = experienceContrastWarnings(draft.theme, draft.pages);
        const said = refusals > 0 || answer.warnings.length > 0 || contrast.length > 0;
        if (!said && !recheckingSave) return null;
        return {
          content: (
            // The previous answer, greyed, while it is read again (`DryRunFrame`).
            <DryRunFrame rechecking={recheckingSave}>
              <ExperienceSaveBlockers
                problems={answer.problems}
                blockers={blockers}
                warnings={answer.warnings}
                contrast={contrast}
                /*
                  « @email est cité page 1, mais saisi page 4 » (D3) : la ligne
                  nomme la variable, et le nom vient du brouillon et du protocole
                  que le dossier nomme - la boîte est rendue par la couche, hors de
                  la portée que les onglets montent, donc la lecture est refaite
                  ici sur la référence du dossier, sous la même clé de cache.
                */
                nameOf={variableNamer({ draft, ...recordScope })}
                pages={draft.pages}
                onOpen={(place) => {
                  saving?.close();
                  onOpenPlace(place);
                }}
                renderProtocolLink={protocolLink(saving?.leave)}
              />
            </DryRunFrame>
          ),
          // Still blocking while it is read again: a vanished refusal would re-arm the button.
          blocked: refusals > 0,
        };
      }}
      onPointerBox={onPointerBox}
      /*
        The SAME rule, on the version the history is aiming production at (§4,
        §9.1). The version's own content and not the draft: « Passer en
        production » from the history moves the pointer onto an old version,
        whose references are not the ones being typed.

        Since #154 the SERVER adds the blockers the cards cannot judge: its dry
        run of that version (`pointerCheck`) answers `analyst-reading-missing`
        and, since #196, `analyst-levels-missing` - a protocol's section Analyste
        living in its versions - so the refusal of §3.6 is drawn BEFORE the click
        instead of arriving as a 409 after it - and, since #158,
        `protocol-variable-orphan`, a direction of the version the protocol's tag
        points at citing an objective it no longer carries (§8.6), its steps living
        there too. Only those three kinds are taken from it: the rest is the pure
        rule's, as before, and the rule on the cards never produces them, so
        nothing is said twice.

        A dry run that FAILS is said, and blocks nothing (review of !474): the
        rule on the cards has spoken, the protocol could not be asked, and the
        409 of the server stays the guard behind the click.
      */
      renderPointerBlockers={(content, tag) => {
        // « Reculer développement » refuses nothing (§4): only production resolves.
        if (tag !== 'production') return null;
        // Nothing from the server before its first answer; the previous one,
        // greyed and still blocking, while it is read again (`DryRunFrame`).
        const answer = pointerCheck.data;
        const rechecking = pointerCheck.isFetching && answer !== undefined;
        const served = (answer?.blockers ?? []).filter(
          (blocker) =>
            blocker.kind === 'analyst-levels-missing' ||
            blocker.kind === 'analyst-reading-missing' ||
            blocker.kind === 'protocol-variable-orphan',
        );
        const unchecked = pointerCheck.isError && !pointerCheck.isFetching;
        const local = referencesRead ? experienceProductionBlockers(content, lookup) : [];
        const blockers = [...local, ...served];
        if (blockers.length === 0 && !unchecked && !rechecking) return null;
        return {
          content: (
            <DryRunFrame rechecking={rechecking}>
              <div className="space-y-3">
                {blockers.length === 0 ? null : (
                  <ExperienceSaveBlockers
                    // The version exists: what is refused is the GESTURE, and
                    // the red title says so (issue #159).
                    gesture="production"
                    problems={[]}
                    blockers={blockers}
                    warnings={[]}
                    contrast={[]}
                    onOpen={onOpenPlace}
                    renderProtocolLink={protocolLink(pointing?.leave)}
                  />
                )}
                {unchecked ? (
                  <Callout tone="warn" data-testid={EXPERIENCE_IDS.pointerCheckFailed}>
                    Vérification impossible : le serveur n’a pas pu dire si le protocole de cette
                    version a une section Analyste complète, ni si ses directions citent des
                    objectifs qui existent. Il tranchera au clic.
                  </Callout>
                ) : null}
              </div>
            </DryRunFrame>
          ),
          blocked: blockers.length > 0,
        };
      }}
      // The two 409s of §9.1, which are not among the six codes of §4.
      ownRefusal={experienceRefusalSentence}
      tabs={[
        {
          id: 'timeline',
          label: 'Timeline',
          render: (binding) => (
            /*
              La portée des variables de §8.6, montée par ONGLET : elle lit le
              protocole que la page protocole du brouillon nomme, pour que la
              scène peigne ses pastilles au nom courant (D5) et que la surcouche
              propose les objectifs (D3). Le brouillon vit dans le `binding`, et
              c'est donc ici, et pas au-dessus de la couche, qu'elle se monte.
            */
            <ExperienceVariableScope draft={binding.draft} experienceId={detail?.id ?? null}>
              <ExperienceTimelineTab
                draft={binding.draft}
                readOnly={binding.readOnly}
                experienceId={detail?.id ?? null}
                selectedPageId={pageId}
                onDraft={binding.onDraft}
                /*
                  Selecting a page is the SAME door « Ouvrir » on a refused line
                  uses, and deliberately: both end as `?tab=timeline&page=<id>`,
                  and a second navigation written here would be a second spelling
                  of one address. It PUSHES rather than replaces - §9.1 wants the
                  browser's back button to land on the page somebody was reading.
                */
                onSelectPage={(nextPage) => onOpenPlace({ tab: 'timeline', pageId: nextPage })}
              />
            </ExperienceVariableScope>
          ),
        },
        {
          id: 'persona',
          label: 'Persona',
          render: (binding) => (
            <ExperienceVariableScope draft={binding.draft} experienceId={detail?.id ?? null}>
              <ExperiencePersonaTab
                draft={binding.draft}
                readOnly={binding.readOnly}
                onDraft={binding.onDraft}
              />
            </ExperienceVariableScope>
          ),
        },
      ]}
    />
  );
}
